// src/pages/webmaster-medic/WebmasterMedicAppointments.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { useDragDropManager } from "react-dnd";
import Modal from "react-modal";
import Swal from "sweetalert2";

import API_BASE_URL from "../../config/apiConfig";
import { useAuth } from "../../context/AuthProvider";
import WebmasterMedicSidebar from "../../components/WebmasterMedicSidebar";
import ExportSchedule from "../../components/ExportSchedule";
import StatusDropdown, { Status } from "../../components/StatusDropdown";
import AppointmentHistoryModal from "../../components/AppointmentHistoryModal";

import avatar from "../../assets/images/icon-avatar.svg";
import setting from "../../assets/images/setting.svg";
import trash from "../../assets/images/trash.svg";
import edit from "../../assets/images/edit-pen.svg";
import clock from "../../assets/images/clock.svg";
import copy from "../../assets/images/copy.svg";
import plusCircle from "../../assets/images/plus-circle.svg";
import phone from "../../assets/images/phone.svg"; // (dacă ai noUnusedLocals=true și nu-l folosești, poți elimina importul)

Modal.setAppElement("#root");

const ItemTypes = { APPOINTMENT: "appointment" } as const;

/* ===================== Types ===================== */

interface TimeInterval {
  start: string; // "09:00"
  end: string;   // "09:30"
}

export interface Appointment {
  id: string;
  therapist_id: string; // user_id al rândului (la noi medici + terapeuți)
  patient_id: string;
  patient_name: string;
  therapist_name?: string;
  type: string;                     // therapy | activity | medical_activity | medical_consult
  appointment_description: string;  // code key
  status?: string;
  start_time: string;               // ISO
  end_time: string;                 // ISO
}

export interface Therapist {
  user_id: string;
  firstname: string;
  lastname: string;
  email: string;
  is_active: boolean;
  therapist_category?: string | null;
  order_in_category?: number | null;
}

export interface Medic {
  user_id: string;
  firstname: string;
  lastname: string;
  email: string;
  is_active: boolean;
}

export interface TherapistCategory {
  key: string;
  value: string;
  priority: string; // numeric string
  color: string;
}

/* dropdown items (ACTIVE) */
interface LabeledItem {
  key: string;
  value: string;
  short_name?: string;
  type?: string;         // pentru status (therapy/activity/medical_*)
  activity_key?: string; // pentru status activity
}

/* fallback maps din admin (pot include inactive) */
type DisplayMap = Record<string, { value?: string; short?: string }>;

/* ===================== Helpers ===================== */

const formatDateToYMD = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const isSameISO = (iso: string, date: string, hhmm: string) =>
  iso === `${date}T${hhmm}:00`;

const hexToRGBA = (hex: string, opacity: number): string => {
  hex = hex.replace("#", "");
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

/* ===================== Page ===================== */

const WebmasterMedicAppointments: React.FC = () => {
  const { token } = useAuth();

  // layout
  const [isCollapsed, setIsCollapsed] = useState<boolean>(() => localStorage.getItem("sidebarCollapsedMedic") === "true");
  const [viewMode, setViewMode] = useState<"scroll" | "full">("scroll");
  const [selectedDate, setSelectedDate] = useState(formatDateToYMD(new Date()));

  // data
  const [therapists, setTherapists] = useState<Therapist[]>([]);
  const [medics, setMedics] = useState<Medic[]>([]);
  const [categories, setCategories] = useState<TherapistCategory[]>([]);
  const [timeIntervals, setTimeIntervals] = useState<TimeInterval[]>([]);
  const [appointments, setAppointments] = useState<Appointment[]>([]);

  // ACTIVE dropdowns
  const [therapyTypes, setTherapyTypes] = useState<LabeledItem[]>([]);
  const [activities, setActivities] = useState<LabeledItem[]>([]);
  const [medicalConsults, setMedicalConsults] = useState<LabeledItem[]>([]);     // consulturi
  const [medicalActivities, setMedicalActivities] = useState<LabeledItem[]>([]); // activități medicale
  const [statuses, setStatuses] = useState<Status[]>([]);

  // DISPLAY MAPS (admin) — fallback daca itemul e inactiv
  const [therapyDisplayMap, setTherapyDisplayMap] = useState<DisplayMap>({});
  const [activityDisplayMap, setActivityDisplayMap] = useState<DisplayMap>({});
  const [statusDisplayMap, setStatusDisplayMap] = useState<DisplayMap>({});

  // program lucru per user (medici + terapeuți)
  const [allowedMap, setAllowedMap] = useState<Record<string, Array<{ start: string; end: string }>>>({});

  // modals
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [copyModalIsOpen, setCopyModalIsOpen] = useState(false);
  const [editModalIsOpen, setEditModalIsOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyApptId, setHistoryApptId] = useState<string | null>(null);

  // add/edit forms
  const [formData, setFormData] = useState({
    therapist_id: "",
    patient_id: "",
    type: "",
    appointment_description: "",
    activity_description: "",
    therapy_type: "",
    medical_activity: "",
    medical_consult: "",
    status: "alocat",
    start_time: "",
    end_time: "",
  });

  const [editFormData, setEditFormData] = useState({
    id: "",
    therapist_id: "",
    patient_id: "",
    type: "",
    appointment_description: "",
    activity_description: "",
    therapy_type: "",
    medical_activity: "",
    medical_consult: "",
    status: "",
    start_time: "",
    end_time: "",
  });

  // patient search
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [editSearchQuery, setEditSearchQuery] = useState("");
  const [editSearchResults, setEditSearchResults] = useState<any[]>([]);

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // 🔒 terapeuții sunt PERMANENT read-only
  const READ_ONLY_THERAPISTS = true;

  /* ------------ fetchers ------------ */

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const r = await fetch(`${API_BASE_URL}/users/therapists`, { headers: { Authorization: `Bearer ${token}` } });
        const data = await r.json();
        setTherapists(Array.isArray(data) ? data : data?.data || []);
      } catch (e) { console.error("Eroare terapeuți:", e); }
    })();
  }, [token]);

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const r = await fetch(`${API_BASE_URL}/users/medics`, { headers: { Authorization: `Bearer ${token}` } });
        const data = await r.json();
        setMedics(Array.isArray(data) ? data : data?.data || []);
      } catch (e) { console.error("Eroare medici:", e); }
    })();
  }, [token]);

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const r = await fetch(`${API_BASE_URL}/types/therapist-categories`, { headers: { Authorization: `Bearer ${token}` } });
        const data = await r.json();
        setCategories(data.data || []);
      } catch (e) { console.error("Eroare categorii:", e); }
    })();
  }, [token]);

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const r = await fetch(`${API_BASE_URL}/types/time-intervals?date=${selectedDate}`, { headers: { Authorization: `Bearer ${token}` } });
        const data = await r.json();
        setTimeIntervals(data.data || []);
      } catch (e) { console.error("Eroare intervale:", e); }
    })();
  }, [token, selectedDate]);

  const fetchAppointments = async (signal?: AbortSignal) => {
    try {
      const r = await fetch(`${API_BASE_URL}/appointments/get-appointments?date=${selectedDate}`, {
        headers: { Authorization: `Bearer ${token}` },
        signal,
      });
      const data = await r.json();
      setAppointments(data.data || []);
    } catch (e) {
      if ((e as any)?.name === "AbortError") return;
      console.error("Eroare programări:", e);
    }
  };
  useEffect(() => {
    if (!token) return;
    const controller = new AbortController();
    fetchAppointments(controller.signal);
    return () => controller.abort();
  }, [token, selectedDate]);

  // ACTIVE dropdowns
  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const [activitiesRes, therapyRes, statusRes, medActRes, medConsRes] = await Promise.all([
          fetch(`${API_BASE_URL}/types/activities`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${API_BASE_URL}/types/therapy-type`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${API_BASE_URL}/types/appointment-status`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${API_BASE_URL}/types/medical-activities`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => null),
          fetch(`${API_BASE_URL}/types/consult-types`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => null),
        ]);

        const [activitiesData, therapyData, statusData] = await Promise.all([
          activitiesRes.json(), therapyRes.json(), statusRes.json()
        ]);

        setActivities(activitiesData.data || []);
        setTherapyTypes(therapyData.data || []);
        setStatuses(statusData.data || []);

        if (medActRes) {
          const d = await medActRes.json();
          setMedicalActivities(d.data || []);
        }
        if (medConsRes) {
          const d = await medConsRes.json();
          setMedicalConsults(d.data || []);
        }
      } catch (e) {
        console.error("Eroare dropdowns:", e);
      }
    })();
  }, [token]);

  // DISPLAY MAPS (fallback pentru afișare value pe chei inactive)
  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const [tAdminRes, aAdminRes, sAdminRes] = await Promise.all([
          fetch(`${API_BASE_URL}/admin/types/therapy-type`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${API_BASE_URL}/admin/types/activities`,   { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${API_BASE_URL}/admin/types/appointment-status`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => null),
        ]);

        const tAdmin = await tAdminRes.json();
        const aAdmin = await aAdminRes.json();

        const tMap: DisplayMap = {};
        (Array.isArray(tAdmin) ? tAdmin : tAdmin?.data || []).forEach((t: any) => {
          const key = t.code ?? t.key;
          tMap[key] = { value: t.value, short: t.short_name };
        });
        setTherapyDisplayMap(tMap);

        const aMap: DisplayMap = {};
        (Array.isArray(aAdmin) ? aAdmin : aAdmin?.data || []).forEach((a: any) => {
          const key = a.code ?? a.key;
          aMap[key] = { value: a.value, short: a.short_name };
        });
        setActivityDisplayMap(aMap);

        if (sAdminRes) {
          const sAdmin = await sAdminRes.json();
          const sMap: DisplayMap = {};
          (Array.isArray(sAdmin) ? sAdmin : sAdmin?.data || []).forEach((s: any) => {
            const key = s.code ?? s.key;
            sMap[key] = { value: s.value, short: s.short_name };
          });
          setStatusDisplayMap(sMap);
        }
      } catch (e) {
        console.warn("Display maps admin nu s-au încărcat:", e);
      }
    })();
  }, [token]);

  // Allowed map per id/day — pentru terapeuți **și** medici
  useEffect(() => {
    type Slot = { start: string; end: string };
    (async () => {
      if (!token) return;
      const ids = [
        ...therapists.map(t => t.user_id),
        ...medics.map(m => m.user_id),
      ];
      if (!ids.length) return;
      try {
        const entries = await Promise.all(
          ids.map(async (id): Promise<[string, Slot[]]> => {
            try {
              const r = await fetch(`${API_BASE_URL}/work-schedule/${id}/day/${selectedDate}`, {
                headers: { Authorization: `Bearer ${token}` },
              });
              const data = await r.json();
              const arr: Slot[] = (data?.data as Slot[]) || [];
              return [id, arr];
            } catch {
              return [id, []];
            }
          })
        );
        const map = Object.fromEntries(entries) as Record<string, Slot[]>;
        setAllowedMap(map);
      } catch (e) {
        console.error("Eroare program lucru:", e);
      }
    })();
  }, [token, selectedDate, therapists, medics]);

  /* ------------ Flags & helpers ------------ */

  const isMedicId = (id: string) => medics.some(m => m.user_id === id);

  // helpers pentru etichete cu fallback
  const getTherapyValue = (key: string) =>
    therapyTypes.find(t => t.key === key)?.value ||
    therapyDisplayMap[key]?.value || key;

  const getActivityValue = (key: string) =>
    activities.find(a => a.key === key)?.value ||
    activityDisplayMap[key]?.value || key;

  const getMedActivityValue = (key: string) =>
    medicalActivities.find(a => a.key === key)?.value || key;

  const getMedConsultValue = (key: string) =>
    medicalConsults.find(a => a.key === key)?.value || key;

  const getStatusValue = (key: string) =>
    statuses.find(s => s.key === key)?.value ||
    statusDisplayMap[key]?.value || key;

  const getTherapyShort = (key: string) =>
    therapyTypes.find(t => t.key === key)?.short_name ||
    therapyDisplayMap[key]?.short ||
    key;

  const getActivityShort = (key: string) =>
    activities.find(a => a.key === key)?.short_name ||
    activityDisplayMap[key]?.short ||
    key;

  const getMedActivityShort = (key: string) =>
    medicalActivities.find(a => a.key === key)?.short_name || key;

  const getMedConsultShort = (key: string) =>
    medicalConsults.find(a => a.key === key)?.short_name || key;

  // verifică dacă un interval e inclus într-un block de program al userului
  const isIntervalAllowed = (userId: string, interval: TimeInterval): boolean => {
    const blocks = allowedMap[userId] || [];
    if (!blocks.length) return false;
    return blocks.some(b => interval.start >= b.start && interval.end <= b.end);
  };

  // poate fi folosită celula pentru add/move (medici + terapeuți — dar terapeuții rămân read-only)
  const canUseCell = (userId: string, interval: TimeInterval): boolean => {
    return isIntervalAllowed(userId, interval);
  };

  /* ------------ Sorting: terapeuți apoi medici ------------ */

  const sortedRows = useMemo(() => {
    const medicalCategory = categories.find(c => c.key === "medical");
    const medicalPriority = medicalCategory ? Number(medicalCategory.priority) : 9999;

    type Row = {
      id: string;
      firstname: string;
      lastname: string;
      is_active: boolean;
      categoryKey?: string;
      orderInCategory?: number;
      isMedic: boolean;
    };

    const therapistRows: Row[] = therapists.map((t) => ({
      id: t.user_id,
      firstname: t.firstname,
      lastname: t.lastname,
      is_active: !!t.is_active,
      categoryKey: t.therapist_category ?? undefined,
      orderInCategory: typeof t.order_in_category === "number" ? t.order_in_category : undefined,
      isMedic: false,
    }));

    const medicRows: Row[] = medics.map((m) => ({
      id: m.user_id,
      firstname: m.firstname,
      lastname: m.lastname,
      is_active: !!m.is_active,
      categoryKey: "medical",
      orderInCategory: 9999,
      isMedic: true,
    }));

    const filtered = (rows: Row[]) => rows.filter((r) => r.is_active);
    const all = [...filtered(therapistRows), ...filtered(medicRows)];

    const getPriority = (row: Row) => {
      if (row.isMedic) return medicalPriority;
      const cat = categories.find(c => c.key === row.categoryKey);
      return cat ? Number(cat.priority) : 999;
    };

    return all.sort((a, b) => {
      const pA = getPriority(a);
      const pB = getPriority(b);
      if (pA !== pB) return pA - pB;
      const oA = a.orderInCategory ?? 9999;
      const oB = b.orderInCategory ?? 9999;
      if (oA !== oB) return oA - oB;
      const nameA = `${a.lastname} ${a.firstname}`.toLowerCase();
      const nameB = `${b.lastname} ${b.firstname}`.toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [therapists, medics, categories, selectedDate]);

  /* ------------ Patient search ------------ */

  const handleSearchPatients = async (query: string) => {
    setSearchQuery(query);
    if (!query || query.length < 3) return setSearchResults([]);
    try {
      const r = await fetch(`${API_BASE_URL}/patient/search?query=${encodeURIComponent(query)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await r.json();
      setSearchResults(data.data || []);
    } catch (e) {
      console.error("search patients:", e);
    }
  };

  const handleSearchEditPatients = async (query: string) => {
    setEditSearchQuery(query);
    if (!query || query.length < 3) return setEditSearchResults([]);
    try {
      const r = await fetch(`${API_BASE_URL}/patient/search?query=${encodeURIComponent(query)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await r.json();
      setEditSearchResults(data.data || []);
    } catch (e) {
      console.error("search patients edit:", e);
    }
  };

  /* ------------ Add / Edit / Delete / Move ------------ */

  const Toast = Swal.mixin({
    toast: true, position: "top-end", showConfirmButton: false, timer: 2000, timerProgressBar: true,
  });

  const handleOpenHistory = (id: string) => {
    setHistoryApptId(id);
    setHistoryOpen(true);
  };

  const handleAddAppointment = (therapistId: string, interval: TimeInterval) => {
    // doar medici pot adăuga; și NUMAI în program
    if (!isMedicId(therapistId)) return;
    if (!isIntervalAllowed(therapistId, interval)) {
      Swal.fire("În afara programului", "Nu poți adăuga programări în afara programului de lucru al medicului.", "warning");
      return;
    }

    const startDateTime = `${selectedDate}T${interval.start}:00`;
    const endDateTime = `${selectedDate}T${interval.end}:00`;

    setFormData({
      therapist_id: therapistId,
      patient_id: "",
      type: "",
      appointment_description: "",
      activity_description: "",
      therapy_type: "",
      medical_activity: "",
      medical_consult: "",
      status: "alocat",
      start_time: startDateTime,
      end_time: endDateTime,
    });

    setSearchQuery("");
    setSearchResults([]);
    setModalIsOpen(true);
  };

  const handleSubmitAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isMedicId(formData.therapist_id)) return;

    // validare defensivă în program
    const interval: TimeInterval = {
      start: formData.start_time.slice(11, 16),
      end: formData.end_time.slice(11, 16),
    };
    if (!isIntervalAllowed(formData.therapist_id, interval)) {
      Swal.fire("În afara programului", "Intervalul selectat nu se află în programul de lucru al medicului.", "error");
      return;
    }

    const finalType = formData.type;
    const finalDescription =
      finalType === "medical_activity" ? formData.medical_activity : formData.medical_consult;

    const body = {
      therapist_id: formData.therapist_id,
      patient_id: formData.patient_id || null,
      type: finalType,
      appointment_description: finalDescription,
      status: formData.status,
      start_time: formData.start_time,
      end_time: formData.end_time,
    };

    try {
      const r = await fetch(`${API_BASE_URL}/appointments/add-appointment`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });

      if (!r.ok) {
        const { detail } = await r.json().catch(() => ({}));
        throw new Error(detail || "Eroare la adăugare.");
      }

      setModalIsOpen(false);
      Toast.fire({ icon: "success", title: "Programare adăugată!" });
      fetchAppointments();
    } catch (e: any) {
      console.error(e);
      Swal.fire("Eroare", e?.message || "Nu s-a putut adăuga.", "error");
    }
  };

  const handleEditOpen = (a: Appointment) => {
    if (!isMedicId(a.therapist_id)) return; // 🔒

    setEditFormData({
      id: a.id,
      therapist_id: a.therapist_id,
      patient_id: a.patient_id,
      type: a.type,
      appointment_description: a.appointment_description,
      activity_description: a.type === "activity" ? a.appointment_description : "",
      therapy_type: a.type === "therapy" ? a.appointment_description : "",
      medical_activity: a.type === "medical_activity" ? a.appointment_description : "",
      medical_consult: a.type === "medical_consult" ? a.appointment_description : "",
      status: a.status || "",
      start_time: a.start_time,
      end_time: a.end_time,
    });
    setEditSearchQuery(a.patient_name || "");
    setEditSearchResults([]);
    setEditModalIsOpen(true);
  };

  const handleSaveEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isMedicId(editFormData.therapist_id)) return;

    // validare defensivă în program
    const interval: TimeInterval = {
      start: editFormData.start_time.slice(11, 16),
      end: editFormData.end_time.slice(11, 16),
    };
    if (!isIntervalAllowed(editFormData.therapist_id, interval)) {
      Swal.fire("În afara programului", "Intervalul selectat nu se află în programul de lucru al medicului.", "error");
      return;
    }

    const finalType = editFormData.type;
    const finalDescription =
      finalType === "medical_activity" ? editFormData.medical_activity : editFormData.medical_consult;

    const body = {
      therapist_id: editFormData.therapist_id,
      patient_id: editFormData.patient_id || null,
      type: finalType,
      appointment_description: finalDescription,
      status: editFormData.status || undefined,
      start_time: editFormData.start_time,
      end_time: editFormData.end_time,
    };

    try {
      const r = await fetch(`${API_BASE_URL}/appointments/${editFormData.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const { detail } = await r.json();
        throw new Error(detail || "Eroare la actualizare.");
      }
      setEditModalIsOpen(false);
      Toast.fire({ icon: "success", title: "Programare actualizată!" });
      fetchAppointments();
    } catch (e: any) {
      console.error(e);
      Swal.fire("Eroare", e?.message || "Nu s-a putut salva.", "error");
    }
  };

  const handleDelete = async (id: string, therapistId: string) => {
    if (!isMedicId(therapistId)) return; // 🔒
    const res = await Swal.fire({
      title: "Ești sigur?",
      text: "Vrei să ștergi această programare?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#6C298B",
      cancelButtonColor: "#dc3545",
      confirmButtonText: "Da, șterge",
      cancelButtonText: "Anulează",
    });
    if (!res.isConfirmed) return;

    try {
      const r = await fetch(`${API_BASE_URL}/appointments/delete/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) {
        const { detail } = await r.json();
        throw new Error(detail || "Eroare la ștergere.");
      }
      Swal.fire("Succes", "Programarea a fost ștearsă.", "success");
      fetchAppointments();
    } catch (e: any) {
      Swal.fire("Eroare", e?.message || "Nu s-a putut șterge.", "error");
    }
  };

  const handleDropAppointment = async (dragged: Appointment, newTherapistId: string, newInterval: TimeInterval) => {
    // mutare permisă doar între medici + doar în programul medicului destinație
    if (!isMedicId(dragged.therapist_id) || !isMedicId(newTherapistId)) return;

    if (!isIntervalAllowed(newTherapistId, newInterval)) {
      Swal.fire("În afara programului", "Nu poți muta programarea în afara programului de lucru al medicului.", "warning");
      return;
    }

    const updated = {
      therapist_id: newTherapistId,
      patient_id: dragged.patient_id || null,
      type: dragged.type,
      appointment_description: dragged.appointment_description,
      status: "mutat",
      start_time: `${selectedDate}T${newInterval.start}:00`,
      end_time: `${selectedDate}T${newInterval.end}:00`,
    };
    try {
      const r = await fetch(`${API_BASE_URL}/appointments/${dragged.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(updated),
      });
      if (!r.ok) throw new Error("Eroare la mutare.");
      fetchAppointments();
    } catch (e) {
      console.error(e);
      Swal.fire("Eroare", "Nu s-a putut muta programarea.", "error");
    }
  };

  // Copiere programări (exista în fișierul inițial; o păstrăm)
  const handleCopyAppointments = async (sourceDate: string, destinationDate: string) => {
    try {
      const r = await fetch(`${API_BASE_URL}/appointments/copy`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ source_date: sourceDate, destination_date: destinationDate, scope: "medic_only" }),
      });
      if (!r.ok) {
        const { detail } = await r.json();
        throw new Error(detail || "Eroare la copiere.");
      }
      Swal.fire({ icon: "success", title: "Programările au fost copiate!" }).then(() => window.location.reload());
    } catch (e: any) {
      Swal.fire("Eroare", e?.message || "Nu s-a putut copia.", "error");
    }
  };

  /* ------------ SMS (opțional — păstrat ca în inițial) ------------ */

  const normalizeSnapshot = (snap: any): any | null => {
  if (!snap) return null;
  if (typeof snap === "object") return snap;
  if (typeof snap === "string") {
    try {
      return JSON.parse(snap);
    } catch {
      return null;
    }
  }
  return null;
};

const fetchLastAuditForAppointment = async (appointmentId: string) => {
  try {
    const r = await fetch(
      `${API_BASE_URL}/admin/appointments/audit?appointment_id=${encodeURIComponent(
        String(appointmentId)
      )}&limit=20&offset=0`,
      { headers: { Authorization: `Bearer ${token}` } }
    );

    const data = await r.json();
    const rows = Array.isArray(data?.data) ? data.data : [];

    // ia primul entry unde s-a schimbat start_time (mutare reală)
    const movedRow =
      rows.find((x: any) => {
        const prev = normalizeSnapshot(x?.prev_snapshot);
        const next = normalizeSnapshot(x?.new_snapshot);
        return (
          prev?.start_time &&
          next?.start_time &&
          prev.start_time !== next.start_time
        );
      }) || null;

    return movedRow || rows[0] || null;
  } catch (e) {
    console.error("fetchLastAuditForAppointment error:", e);
    return null;
  }
};


  const getParentPhoneByPatient = async (patientId: string): Promise<string | null> => {
    try {
      const response = await fetch(`${API_BASE_URL}/patient/${patientId}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await response.json();
      return data?.parent_phone || null;
    } catch { return null; }
  };

  const sendSMS = async (recipientNumber: string, messageBody: string) => {
  try {
    const r = await fetch(`${API_BASE_URL}/sms/send`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ to: recipientNumber, body: messageBody }),
    });

    const data = await r.json();
    if (!r.ok) throw new Error(data?.detail || "SMS failed");

    Swal.fire("Succes", "SMS trimis cu succes!", "success");
  } catch (err) {
    console.error("SMS error:", err);
    Swal.fire("Eroare", "A apărut o eroare la trimiterea SMS-ului", "error");
  }
};

const getAppointmentLabelForSMS = (app: Appointment) => {
  if (app.type === "therapy") return getTherapyValue(app.appointment_description);
  if (app.type === "activity") return getActivityValue(app.appointment_description);
  if (app.type === "medical_activity") return getMedActivityValue(app.appointment_description);
  if (app.type === "medical_consult") return getMedConsultValue(app.appointment_description);
  return app.appointment_description;
};

const handleNotificareProgram = async (app: Appointment) => {
  const phone = await getParentPhoneByPatient(app.patient_id);
  if (!phone) {
    return Swal.fire("Eroare", "Nu s-a găsit numărul de telefon al părintelui.", "error");
  }

  const dateObj = new Date(app.start_time);

  const ziua = dateObj.toLocaleDateString("ro-RO", { weekday: "long" });
  const ziuaCapitalized = ziua.charAt(0).toUpperCase() + ziua.slice(1);

  const data = dateObj.toLocaleDateString("ro-RO", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });

  const ora = app.start_time.slice(11, 16);
  const denumire = getAppointmentLabelForSMS(app);

const isConsult = app.type === "medical_consult";

const acteMsg = isConsult
  ? `Să aveți la dvs. buletin pacient/ reprezentant legal, certificat de naștere pacient minor și acte medicale dacă există. `
  : "";

const telefon = isConsult ? "0743 893 625" : "0729 456 222";

const msg =
  `Vă reamintim că ${ziuaCapitalized}, în data de ${data}, ora ${ora} ` +
  `aveți programare la Clinica Maria Beatrice pentru ${denumire}. ` +
  acteMsg +
  `Dacă nu puteți ajunge, reprogramați la nr. de tel. ${telefon}. ` +
  `Adresa: Alba Iulia, str. Ana Aslan, nr. 34.`;
  sendSMS(phone, msg);
};

const handleNotificareModificare = async (app: Appointment) => {
  const phone = await getParentPhoneByPatient(app.patient_id);
  if (!phone) {
    return Swal.fire("Eroare", "Nu s-a găsit numărul de telefon al părintelui.", "error");
  }

  // Luăm audit-ul ca să aflăm ora veche + ora nouă
  const audit = await fetchLastAuditForAppointment(app.id);

  const prev = normalizeSnapshot(audit?.prev_snapshot);
  const next = normalizeSnapshot(audit?.new_snapshot);

  // oldStart din prev_snapshot, newStart din new_snapshot (fallback la app.start_time)
  const oldStartISO: string | null = prev?.start_time || null;
  const newStartISO: string = next?.start_time || app.start_time;

  const data = new Date(newStartISO).toLocaleDateString("ro-RO", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });

  const oraVeche = oldStartISO ? oldStartISO.slice(11, 16) : null;
  const oraNoua = newStartISO ? newStartISO.slice(11, 16) : null;

  // fallback safe dacă nu avem ora veche din audit
  const msg =
    oraVeche && oraNoua
      ? `Vă informăm că programarea dvs. din data de ${data}, ora ${oraVeche} a fost mutată la ora ${oraNoua}. ` +
        `Clinica Maria Beatrice, Alba Iulia, str. Ana Aslan, nr. 34.`
      : `Vă informăm că programarea dvs. din data de ${data} a fost mutată. ` +
        `Clinica Maria Beatrice, Alba Iulia, str. Ana Aslan, nr. 34.`;

  sendSMS(phone, msg);
};

  /* ------------ DnD helpers ------------ */

  const AutoScroller: React.FC<{ scrollContainerRef: React.RefObject<HTMLDivElement | null> }> = ({ scrollContainerRef }) => {
    const dragDropManager = useDragDropManager();
    useEffect(() => {
      const interval = 20, margin = 80, speed = 15;
      const tick = () => {
        const monitor = dragDropManager.getMonitor();
        if (!monitor.isDragging()) return;
        const point = monitor.getClientOffset();
        const container = scrollContainerRef.current;
        if (!point || !container) return;
        const { top, bottom, left, right } = container.getBoundingClientRect();
        if (point.x < left + margin) container.scrollLeft -= speed;
        else if (point.x > right - margin) container.scrollLeft += speed;
        if (point.y < top + margin) container.scrollTop -= speed;
        else if (point.y > bottom - margin) container.scrollTop += speed;
        const winMargin = 60;
        if (point.y < winMargin && window.scrollY) window.scrollBy(0, -speed);
        else if (point.y > window.innerHeight - winMargin) window.scrollBy(0, speed);
      };
      const id = setInterval(tick, interval);
      return () => clearInterval(id);
    }, [dragDropManager, scrollContainerRef]);
    return null;
  };

  const DraggableAppointment: React.FC<{
    a: Appointment;
    onEdit: (a: Appointment) => void;
    onDelete: (id: string, therapistId: string) => void;
    onOpenHistory: (id: string) => void;
  }> = ({ a, onEdit, onDelete, onOpenHistory }) => {
    const isMed = isMedicId(a.therapist_id);

    const [{ isDragging }, drag] = useDrag({
      type: ItemTypes.APPOINTMENT,
      item: a,
      canDrag: () => isMed, // 🔒 NU drag pe terapeuți
      collect: (m) => ({ isDragging: m.isDragging() }),
    });

    const [dropdownOpen, setDropdownOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const divRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      const onDocClick = (ev: MouseEvent) => {
        if (
          dropdownRef.current &&
          !dropdownRef.current.contains(ev.target as Node) &&
          divRef.current &&
          !divRef.current.contains(ev.target as Node)
        ) {
          setDropdownOpen(false);
        }
      };
      document.addEventListener("mousedown", onDocClick);
      return () => document.removeEventListener("mousedown", onDocClick);
    }, []);

    drag(divRef);

const isActivity = a.type === "activity";
const isTherapy = a.type === "therapy";
const isMedActivity = a.type === "medical_activity";
const isMedConsult = a.type === "medical_consult";

// ✅ acoperă toate tipurile + fallback dacă nu ai încă statusuri definite pentru medical
    const filteredStatuses = isActivity
      ? statuses.filter(s => s.type === "activity" && s.activity_key === a.appointment_description)
      : statuses.filter(s => s.type === "therapy");

    return (
      <div
        ref={divRef}
        className="appointment-block"
        style={{ opacity: isDragging ? 0.5 : 1, cursor: isMed ? "move" : "default" }}
      >
        {isTherapy ? (
          <>
            <div className="appointment-value appointment-patient">
              <span className="appointment-text">Pacient:</span> {a.patient_name}
            </div>

            {viewMode === "scroll" ? (
              <div className="appointment-value appointment-type">
                <span className="appointment-text">Terapie:</span>{" "}
                {getTherapyValue(a.appointment_description)}
              </div>
            ) : (
              <div className="appointment-value appointment-type appointment-short">
                <span className="appointment-text">Terapie:</span>{" "}
                {getTherapyShort(a.appointment_description)}
                <div className="appointment-value appointment-status">
                  <span className="appointment-text">Status:</span>
                  {isMed ? (
                    <StatusDropdown
                      statuses={filteredStatuses}
                      currentStatus={a.status || ""}
                      onChange={async (st) => {
                        try {
                          const r = await fetch(`${API_BASE_URL}/appointments/update-status/${a.id}`, {
                            method: "PUT",
                            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                            body: JSON.stringify({ status: st }),
                          });
                          if (!r.ok) throw new Error("Eroare status.");
                          fetchAppointments();
                        } catch {
                          Swal.fire("Eroare", "Nu s-a putut actualiza statusul.", "error");
                        }
                      }}
                    />
                  ) : (
                    <span className="status-readonly">{getStatusValue(a.status || "")}</span>
                  )}
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            <div className="appointment-value appointment-patient">
              <span className="appointment-text">Pacient:</span> {a.patient_name}
            </div>
            <div className="appointment-value appointment-type">
              {viewMode === "scroll" ? (
                <>
                  <span className="appointment-text">
                    {isMedActivity ? "Activitate medicală:" : isMedConsult ? "Consult medical:" : "Activitate:"}
                  </span>{" "}
                  {isMedActivity
                    ? getMedActivityValue(a.appointment_description)
                    : isMedConsult
                    ? getMedConsultValue(a.appointment_description)
                    : getActivityValue(a.appointment_description)}
                </>
              ) : (
                <div className="appointment-value appointment-type appointment-short">
                  <span className="appointment-text">
                    {isMedActivity ? "Activitate medicală:" : isMedConsult ? "Consult medical:" : "Activitate:"}
                  </span>{" "}
                  {isMedActivity
                    ? getMedActivityShort(a.appointment_description)
                    : isMedConsult
                    ? getMedConsultShort(a.appointment_description)
                    : getActivityShort(a.appointment_description)}
                  <div className="appointment-value appointment-status status-short">
                    <span className="appointment-text">Status:</span>
                     {isMed ? (
                      <StatusDropdown
                        statuses={filteredStatuses}
                        currentStatus={a.status || ""}
                        onChange={async (st) => {
                          try {
                            const r = await fetch(`${API_BASE_URL}/appointments/update-status/${a.id}`, {
                              method: "PUT",
                              headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                              body: JSON.stringify({ status: st }),
                            });
                            if (!r.ok) throw new Error("Eroare status.");
                            fetchAppointments();
                          } catch {
                            Swal.fire("Eroare", "Nu s-a putut actualiza statusul.", "error");
                          }
                        }}
                        viewMode={viewMode}
                      />
                    ) : (
                      <span className="status-readonly">{getStatusValue(a.status || "")}</span>
                    )}
                  </div>
                </div>
              )}
            </div>

            {a.appointment_description === "evaluare" && (
              <div className="appointment-value appointment-patient">
                <span className="appointment-text">Pacient:</span> {a.patient_name}
              </div>
            )}
          </>
        )}


    {/* În modul scroll, statusul se afișează separat (ca în exemplul tău) */}
    {viewMode === "scroll" ? (
      <div className="appointment-value appointment-status">
        <span className="appointment-text">Status:</span>
        {isMed ? (
          <StatusDropdown
            statuses={filteredStatuses}
            currentStatus={a.status || ""}
            onChange={async (st) => {
              try {
                const r = await fetch(`${API_BASE_URL}/appointments/update-status/${a.id}`, {
                  method: "PUT",
                  headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                  body: JSON.stringify({ status: st }),
                });
                if (!r.ok) throw new Error("Eroare status.");
                fetchAppointments();
              } catch {
                Swal.fire("Eroare", "Nu s-a putut actualiza statusul.", "error");
              }
            }}
            viewMode={viewMode}
          />
        ) : (
          <span className="status-readonly">{getStatusValue(a.status || "")}</span>
        )}
      </div>
    ) : null}

        <div className="setting-dropdown-wrapper">
          <button
            className="setting-button"
            onClick={(e) => {
              e.stopPropagation();
              setDropdownOpen(!dropdownOpen);
            }}
          >
            <img src={setting} alt="setări" />
          </button>
          {dropdownOpen && (
            <div className="dropdown-menu" ref={dropdownRef}>
              {isMed && (
                <>
                  <button onClick={() => onDelete(a.id, a.therapist_id)}>
                    <img src={trash} alt="sterge" className="delete-icon" /> Șterge
                  </button>
                  <button onClick={() => onEdit(a)}>
                    <img src={edit} alt="edit" className="edit-icon" /> Editează
                  </button>
                </>
              )}
              <button onClick={() => onOpenHistory(a.id)}>
                <img src={clock} alt="history" className="history-icon" /> Istoric
              </button>
              {isMed && a.patient_id && a.status === "alocat" && (a.type === "medical_consult" || a.type === "medical_activity") && (
                <button onClick={() => handleNotificareProgram(a)}>
                  <img src={phone} alt="notificare" className="notify-icon" /> Notifică părinte
                </button>
              )}

              {isMed && a.patient_id && a.status === "mutat" && (a.type === "medical_consult" || a.type === "medical_activity") && (
                <button onClick={() => handleNotificareModificare(a)}>
                  <img src={phone} alt="notificare" className="notify-icon" /> Notifică părinte
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  const AppointmentCell: React.FC<{
    therapistId: string;
    interval: TimeInterval;
    selectedDate: string;
  }> = ({ therapistId, interval, selectedDate }) => {
    const cellRef = useRef<HTMLDivElement>(null);

    // țintă = rândul pe care se adaugă/mută
    const isTargetMedic = isMedicId(therapistId);

    // este în program + e medic? doar atunci permitem add/move
    const inWorkHours = canUseCell(therapistId, interval);
    const allowedForTarget = isTargetMedic && inWorkHours;

    const cellApps = appointments.filter(
      (a) => a.therapist_id === therapistId && isSameISO(a.start_time, selectedDate, interval.start)
    );

    const [, drop] = useDrop<Appointment>({
      accept: ItemTypes.APPOINTMENT,
      canDrop: () => allowedForTarget && cellApps.length < 2,
      drop: (dragged) => {
        if (allowedForTarget && cellApps.length < 2) {
          handleDropAppointment(dragged, therapistId, interval);
        }
      },
    });

    drop(cellRef);

    return (
      <div
        ref={cellRef}
        className={`appointment-cell ${allowedForTarget ? "" : "cell-disabled"}`}
        onClick={() => {
          if (allowedForTarget && cellApps.length === 0) handleAddAppointment(therapistId, interval);
        }}
        title={allowedForTarget ? "" : "În afara programului / doar vizualizare"}
      >
        <div className="appointment-cell-content">
          {cellApps.map((a) => (
            <DraggableAppointment
              key={a.id}
              a={a}
              onEdit={handleEditOpen}
              onDelete={handleDelete}
              onOpenHistory={handleOpenHistory}
            />
          ))}

          {/* Butoanele de add apar DOAR la medici și DOAR în program */}
          {allowedForTarget && cellApps.length === 0 && (
            <button
              className="add-appointment"
              onClick={(e) => {
                e.stopPropagation();
                handleAddAppointment(therapistId, interval);
              }}
            >
              Adaugă programare
            </button>
          )}

          {allowedForTarget && cellApps.length === 1 && (
            <button
              className="add-appointment-plus"
              onClick={(e) => {
                e.stopPropagation();
                handleAddAppointment(therapistId, interval);
              }}
            >
              <img src={plusCircle} alt="adauga" className="add-new-icon" />
            </button>
          )}
        </div>
      </div>
    );
  };

  /* ------------ Copy modal state ------------ */
  const [sourceDate, setSourceDate] = useState("");
  const [destinationDate, setDestinationDate] = useState("");

  /* ------------ Render ------------ */

  return (
    <DndProvider backend={HTML5Backend}>
      <AutoScroller scrollContainerRef={scrollContainerRef} />
      <div className={`dashboard-container ${isCollapsed ? "collapsed" : ""}`}>
        <WebmasterMedicSidebar onCollapseChange={setIsCollapsed} />
        <div className="page-content">
          <h1 className="page-title">Programări cabinet medical</h1>

          <div className="table-top">
            <div className="page-subtitle">Programări</div>
            <div className="appointments-actions">
              <div className="copy-appointments">
                <button className="primary-btn btn-copy-appointments" onClick={() => setCopyModalIsOpen(true)}>
                  <img src={copy} alt="copiaza" /> Copiază programări
                </button>
              </div>
              <div className="export-appointments">
                <ExportSchedule startDate={selectedDate} endDate={selectedDate} />
              </div>
              <div className="appointment-date">
                <label>Data:</label>
                <input
                  type="date"
                  min="2025-01-01"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="mode-buttons">
            <button className={viewMode === "scroll" ? "mod-active" : ""} onClick={() => setViewMode("scroll")}>
              Mod scroll
            </button>
            <button className={viewMode === "full" ? "mod-active" : ""} onClick={() => setViewMode("full")}>
              Mod complet
            </button>
          </div>

          <div className={viewMode === "scroll" ? "scrollable-container" : "full-width-container"} ref={scrollContainerRef}>
            <div className="appointments-table">
              <div className="table-header">
                <div className="therapist-header">Nume / Prenume</div>
                <div className="appointments-header-scroll">
                  {timeIntervals.map((interval) => (
                    <div key={interval.start} className="appointment-header-cell">
                      {interval.start} - {interval.end}
                    </div>
                  ))}
                </div>
              </div>

              <div className="table-body">
                {sortedRows.map((row) => {
                  const therapistObj = therapists.find(t=>t.user_id===row.id);
                  const catKey = row.isMedic ? "medical" : therapistObj?.therapist_category;
                  const cat = categories.find((c) => c.key === catKey);
                  const bgColor = cat ? hexToRGBA(cat.color, 1) : "transparent";
                  return (
                    <div key={row.id} className="appointment-row">
                      <div className="therapist-cell" style={{ backgroundColor: bgColor }}>
                        <img src={avatar} alt="user" className="user-icon" /> {row.firstname} {row.lastname}
                        {row.isMedic && <span style={{ marginLeft: 8, fontSize: 12, opacity: 0.8 }}>(medic)</span>}
                      </div>
                      <div className="appointments-row-scroll">
                        {timeIntervals.map((interval) => (
                          <AppointmentCell
                            key={`${row.id}-${interval.start}`}
                            therapistId={row.id}
                            interval={interval}
                            selectedDate={selectedDate}
                          />
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Istoric */}
          <AppointmentHistoryModal
            appointmentId={historyApptId}
            isOpen={historyOpen}
            onRequestClose={() => setHistoryOpen(false)}
          />

          {/* Copy modal */}
          <Modal isOpen={copyModalIsOpen} onRequestClose={() => setCopyModalIsOpen(false)}>
            <h2 className="modal-title">Copiază programări</h2>
            <div className="form-copy">
              <div className="copy-date">
                <label>Data sursă:</label>
                <input type="date" min="2025-01-01" value={sourceDate} onChange={(e) => setSourceDate(e.target.value)} />
              </div>
              <div className="copy-date">
                <label>Data destinație:</label>
                <input type="date" min="2025-01-01" value={destinationDate} onChange={(e) => setDestinationDate(e.target.value)} />
              </div>
              <div className="form-row form-actions">
                <button className="cancel-btn" onClick={() => setCopyModalIsOpen(false)} type="button">Anulează</button>
                <button className="primary-btn" onClick={() => handleCopyAppointments(sourceDate, destinationDate)}>Copiază</button>
              </div>
            </div>
          </Modal>

          {/* Add modal */}
          <Modal isOpen={modalIsOpen} onRequestClose={() => setModalIsOpen(false)}>
            <h2 className="modal-title">Adaugă programare</h2>
            <form className="add-appointmen-form" onSubmit={handleSubmitAdd}>
              <div className="form-row">
                <label>Tip programare:</label>
                <select
                  value={formData.type}
                  onChange={(e) =>
                    setFormData((p) => ({
                      ...p,
                      type: e.target.value,
                      appointment_description: "",
                      activity_description: "",
                      therapy_type: "",
                      medical_activity: "",
                      medical_consult: "",
                      // resetăm și pacientul când schimbăm tipul
                      patient_id: "",
                    }))
                  }
                  required
                >
                  <option value="">Selectează tipul</option>
                  <option value="medical_activity">Activitate medicală</option>
                  <option value="medical_consult">Consult medical</option>
                </select>
              </div>

              {(formData.type === "medical_consult" ||
                (formData.type === "medical_activity" && formData.medical_activity.toLowerCase().includes("evaluare"))) && (
                <div className="form-row autocomplete-wrapper">
                  <label>Pacient:</label>
                  <input
                    type="text"
                    placeholder="Caută pacient (nume sau CNP)"
                    value={searchQuery}
                    onChange={(e) => handleSearchPatients(e.target.value)}
                    required={formData.type === "medical_consult"}
                  />
                  {searchResults.length > 0 && (
                    <ul className="autocomplete-list" style={{ border: "1px solid #ccc" }}>
                      {searchResults.map((patient: any) => (
                        <li
                          key={patient.id}
                          onClick={() => {
                            setFormData((prev) => ({ ...prev, patient_id: String(patient.id) }));
                            setSearchQuery(`${patient.firstname} ${patient.lastname}`);
                            setSearchResults([]);
                          }}
                          style={{ cursor: "pointer" }}
                        >
                          {patient.firstname} {patient.lastname} - {patient.cnp}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              {formData.type === "medical_activity" && (
                <div className="form-row">
                  <label>Activitate medicală:</label>
                  <select
                    value={formData.medical_activity}
                    onChange={(e) => setFormData((p) => ({ ...p, medical_activity: e.target.value }))}
                    required
                  >
                    <option value="">Selectează activitatea</option>
                    {medicalActivities.map((a) => (
                      <option key={a.key} value={a.key}>
                        {a.value}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {formData.type === "medical_consult" && (
                <div className="form-row">
                  <label>Consult medical:</label>
                  <select
                    value={formData.medical_consult}
                    onChange={(e) => setFormData((p) => ({ ...p, medical_consult: e.target.value }))}
                    required
                  >
                    <option value="">Selectează tipul</option>
                    {medicalConsults.map((c) => (
                      <option key={c.key} value={c.key}>
                        {c.value}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="form-row form-actions">
                <button className="cancel-btn" onClick={() => setModalIsOpen(false)} type="button">Anulează</button>
                <button className="primary-btn" type="submit">Adaugă programare</button>
              </div>
            </form>
          </Modal>

          {/* Edit modal (medici) */}
          <Modal isOpen={editModalIsOpen} onRequestClose={() => setEditModalIsOpen(false)}>
            <h2 className="modal-title">Editează programare</h2>
            <form className="edit-appointment-form" onSubmit={handleSaveEdit}>
              <div className="form-row">
                <label>Tip programare:</label>
                <select
                  value={editFormData.type}
                  onChange={(e) =>
                    setEditFormData((p) => ({
                      ...p,
                      type: e.target.value,
                      appointment_description: "",
                      activity_description: "",
                      therapy_type: "",
                      medical_activity: "",
                      medical_consult: "",
                      // reset pacient dacă schimb tipul
                      patient_id: "",
                    }))
                  }
                  required
                >
                  <option value="">Selectează tipul</option>
                  <option value="medical_activity">Activitate medicală</option>
                  <option value="medical_consult">Consult medical</option>
                </select>
              </div>

              {(editFormData.type === "medical_consult" ||
                (editFormData.type === "medical_activity" && (editFormData.medical_activity || "").toLowerCase().includes("evaluare"))) && (
                <div className="form-row autocomplete-wrapper">
                  <label>Pacient:</label>
                  <input
                    type="text"
                    placeholder="Caută pacient (nume sau CNP)"
                    value={editSearchQuery}
                    onChange={(e) => handleSearchEditPatients(e.target.value)}
                    required={editFormData.type === "medical_consult"}
                  />
                  {editSearchResults.length > 0 && (
                    <ul className="autocomplete-list" style={{ border: "1px solid #ccc" }}>
                      {editSearchResults.map((patient: any) => (
                        <li
                          key={patient.id}
                          onClick={() => {
                            setEditFormData((prev) => ({ ...prev, patient_id: String(patient.id) }));
                            setEditSearchQuery(`${patient.firstname} ${patient.lastname}`);
                            setEditSearchResults([]);
                          }}
                          style={{ cursor: "pointer" }}
                        >
                          {patient.firstname} {patient.lastname} - {patient.cnp}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              {editFormData.type === "medical_activity" && (
                <div className="form-row">
                  <label>Activitate medicală:</label>
                  <select
                    value={editFormData.medical_activity}
                    onChange={(e) => setEditFormData((p) => ({ ...p, medical_activity: e.target.value }))}
                    required
                  >
                    <option value="">Selectează activitatea</option>
                    {medicalActivities.map((a) => (
                      <option key={a.key} value={a.key}>
                        {a.value}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {editFormData.type === "medical_consult" && (
                <div className="form-row">
                  <label>Consult medical:</label>
                  <select
                    value={editFormData.medical_consult}
                    onChange={(e) => setEditFormData((p) => ({ ...p, medical_consult: e.target.value }))}
                    required
                  >
                    <option value="">Selectează tipul</option>
                    {medicalConsults.map((c) => (
                      <option key={c.key} value={c.key}>
                        {c.value}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="form-row form-actions">
                <button className="cancel-btn" onClick={() => setEditModalIsOpen(false)} type="button">Anulează</button>
                <button className="primary-btn" type="submit">Salvează</button>
              </div>
            </form>
          </Modal>
        </div>
      </div>
    </DndProvider>
  );
};

export default WebmasterMedicAppointments;